import type { HubGame } from '@/types';

export interface SlotCopySection {
  heading: string;
  paragraphs?: string[];
  bullets?: string[];
}

export interface SlotCopy {
  /** One-line pitch, shown directly under the title. */
  lead: string;
  intro: string[];
  sections: SlotCopySection[];
}

const ANTE_BET =
  '**Ante Bet** raises the stake from 20x to 25x and doubles the Free Spins entry chance in the base game. Buy Free Spins is disabled while Ante Bet is on.';
const BUY_FREE_SPINS =
  '**Buy Free Spins** instantly starts a Free Spins round from the base game at the price set in game config. Ante Bet must be off.';
const FEATURE_ORDER =
  '**Feature order.** If more than one feature is won on the same spin, they play one after another. When Money Spins appear during Free Spins, Free Spins continue afterward.';

/**
 * Editorial sheet per slot. The catalog only carries math fields, so the
 * game write-up lives here. Keys are title/id/symbol slugs; `**bold**`
 * marks a paragraph lead-in.
 */
const SLOT_COPY: Record<string, SlotCopy> = {
  'bevel-queen': {
    lead: 'Unlock the top row of a 6×6 pay-anywhere grid, tumble matching gems, and stack multipliers in Free Spins for wins up to 25,000x.',
    intro: [
      'Bevel Queen is a 6×6 pay-anywhere slot. Identical symbols pay by how many land anywhere on the playable screen after a spin or tumble. The top row starts locked. Each winning tumble counts toward unlocking those cells, left to right, expanding the grid from 30 to 36 positions. Four or more scatters unlock 10 Free Spins, where multiplier symbols stay through the tumble, add together, and boost that sequence’s total win. Unlocked top cells stay open for the rest of the Free Spins round.',
    ],
    sections: [
      {
        heading: 'What to Expect',
        bullets: [
          'The locked top row unlocks during tumbles at counts 1, 3, 6, 9, 12, and 15. Unlocks persist through Free Spins and reset on a new base-game spin.',
          'Multiplier symbols appear only in Free Spins (up to 500x). They stay on screen during tumbles, then are added together and applied to that tumble’s total win.',
          'Four or more scatters unlock 10 Free Spins. Three or more scatters during the bonus award five more spins. Ante Bet raises the stake from 20x to 25x and doubles the Free Spins entry chance in the base game. Buy Free Spins is available at the price set in game config.',
        ],
      },
      {
        heading: 'Unlock and Free Spins',
        paragraphs: [
          'The grid is 6 reels × 6 rows. At the start of each base-game spin the top row is locked (30 playable cells). Winning tumbles raise a tumble count. Columns open left to right when that count reaches 1, 3, 6, 9, 12, and 15. Newly unlocked cells can land symbols and pay. A new paid base spin relocks the top row. During Free Spins, unlocked cells stay open for the rest of the round.',
          'Landing four or more Scatter symbols in the base game awards 10 Free Spins. During Free Spins, multiplier symbols can land with a random value from 2x, 3x, 4x, 5x, 6x, 8x, 10x, 12x, 15x, 20x, 25x, 50x, 100x, 200x, or 500x. They do not appear in the base game. Multipliers remain until the current tumble sequence ends. All multiplier values on screen are then added together, and that combined value multiplies the tumble’s total win.',
          'Three or more Scatters during Free Spins award five additional Free Spins. Retriggers can happen more than once.',
          'If the win reaches 25,000x the base bet, the round ends immediately, the cap is paid, and any remaining Free Spins are forfeited.',
          ANTE_BET,
          BUY_FREE_SPINS,
        ],
      },
    ],
  },

  'casino-neon': {
    lead: 'Light up a 5×3 neon casino: 25 fixed paylines, a giant 3×3 block in Free Spins, and Money Spins that lock prizes for respins. Wins up to 5,000x.',
    intro: [
      'Casino Neon is a 5×3 slot with 25 fixed paylines, left to right from the leftmost reel. Money symbols can lock prizes and start Money Spins. Scatters unlock Free Spins, where a giant 3×3 symbol block covers the center of the reels. If both features hit on the same spin, Money Spins play first; if Money Spins land during Free Spins, Free Spins continue afterward.',
    ],
    sections: [
      {
        heading: 'What to Expect',
        bullets: [
          'Six or more Money symbols trigger Money Spins. Prizes stay locked while the remaining positions respin. Each new Money symbol refreshes 3 chances. Filling the grid awards the MEGA prize.',
          'Free Spins add a giant 3×3 symbol block in the center of the reels for a bigger feature round.',
          'Buy Free Spins starts the bonus from the base game at the price set in game config. Ante Bet raises the stake from 20x to 25x, doubles the Free Spins entry chance in the base game, and disables buy.',
        ],
      },
      {
        heading: 'Money Spins and Free Spins',
        paragraphs: [
          '**Money Spins.** Landing 6 or more Money symbols starts the feature with 3 chances. Money prizes stay in place. Empty positions respin. A new Money symbol locks in and refreshes chances to 3. The round ends when chances run out or the grid is full. Prize labels: MINI (30x bet), MAJOR (100x bet), and MEGA for filling the feature grid.',
          '**Free Spins.** Scatter symbols unlock Free Spins. The round awards 10 Free Spins, with extra spins available on retrigger. A giant 3×3 block covers the center reels (2–4, all rows); the side cells keep spinning. Extra Scatters during the feature can add more spins.',
          FEATURE_ORDER,
          BUY_FREE_SPINS,
          ANTE_BET,
          'If the win reaches 5,000x the bet, the round ends and the cap is paid.',
        ],
      },
    ],
  },

  'cat-plush': {
    lead: 'Watch the garden bloom on a cozy 3×3: five paylines, Bloom Respin when two reels match, and the Garden Fortune Wheel when the grid fills. Wins up to 5,000x.',
    intro: [
      'Cat Plush is a 3×3 slot with 5 fixed paylines, left to right. Only the highest win per line is paid; multiple line wins are added together. Wild substitutes for any paying symbol. There are no Free Spins, no bonus buy, and no ante. The game is portrait-first.',
    ],
    sections: [
      {
        heading: 'What to Expect',
        bullets: [
          'When two full reels land the same plush and the third does not, those reels lock and the remaining reel respins until a payline wins (Bloom Respin).',
          'Fill all nine positions with a single type (Wilds may help) to spin the Garden Fortune Wheel. Lower tier: 2×, 3×, 4×, 5×, or UP. Upper tier: 7×, 8×, 10×, 12×, or 20×.',
          'Maximum win is 5,000x total bet. Low volatility. No Free Spins, buy, or ante.',
        ],
      },
      {
        heading: 'Bloom Respin and Garden Fortune Wheel',
        paragraphs: [
          '**Bloom Respin.** If exactly two reels are full stacks of the same symbol and the third is not, those two reels lock. The remaining reel respins until at least one payline wins. If the initial grid already has a line win, Bloom Respin is skipped. Bloom Respin resolves first, then all five paylines are evaluated on the final grid.',
          '**Garden Fortune Wheel.** If the full 3×3 grid shows one symbol type (Wilds may complete it, including nine Wilds), the Garden Fortune Wheel spins and multiplies the total line wins. Landing on UP advances to the higher multiplier tier.',
          'If the win reaches 5,000x total bet, the round ends and the cap is paid.',
        ],
      },
    ],
  },

  'cosmic-jackpot': {
    lead: 'Chase cosmic prizes on a 5×3 grid: 25 fixed paylines, a giant 3×3 block in Free Spins, and Money Spins with MINI, MAJOR, and MEGA jackpots. Wins up to 5,000x.',
    intro: [
      'Cosmic Jackpot is a 5×3 slot with 25 fixed paylines, left to right from the leftmost reel. Money symbols can lock prizes and start Money Spins. Scatters unlock Free Spins, where a giant 3×3 symbol block covers the center of the reels. If both features hit on the same spin, Money Spins play first; if Money Spins land during Free Spins, Free Spins continue afterward.',
    ],
    sections: [
      {
        heading: 'What to Expect',
        bullets: [
          'Six or more Money symbols trigger Money Spins. Prizes stay locked while the remaining positions respin. Each new Money symbol refreshes 3 chances. Filling the grid awards MEGA (1,000x).',
          'Jackpot prizes: MINI 25x, MAJOR 100x, MEGA 1,000x (fill the feature grid).',
          'Free Spins add a giant 3×3 symbol block in the center. Ante Bet doubles the Free Spins entry chance in the base game. Buy Free Spins starts the bonus at the price set in game config.',
        ],
      },
      {
        heading: 'Money Spins and Free Spins',
        paragraphs: [
          '**Money Spins.** Landing 6 or more Money symbols starts the feature with 3 chances. Money prizes stay in place. Empty positions respin. A new Money symbol locks in and refreshes chances to 3. The round ends when chances run out or the grid is full. Jackpots: MINI 25x, MAJOR 100x, MEGA 1,000x for filling the feature grid.',
          '**Free Spins.** Scatter symbols unlock Free Spins. The round awards 10 Free Spins, with extra spins available on retrigger. A giant 3×3 block covers the center reels (2–4, all rows); the side cells keep spinning.',
          FEATURE_ORDER,
          BUY_FREE_SPINS,
          ANTE_BET,
          'If the win reaches 5,000x the bet, the round ends and the cap is paid.',
        ],
      },
    ],
  },

  'cute-crush': {
    lead: 'Crush matching treats with Cookie Man on a candy-packed 6×5 grid, then stack bomb multipliers in Free Spins for wins up to 10,000x.',
    intro: [
      'Cute Crush is a 6×5 pay-anywhere slot. Identical symbols pay by how many land anywhere on the screen after a spin or tumble — no paylines required. Winning treats disappear, the rest fall, and new symbols drop in for as long as wins keep forming. Four or more scatters unlock 10 Free Spins, where bomb multipliers (up to 100x each) stay through the tumble, add together, and boost that sequence’s total win. Cookie Man hosts the grid, including gold-scatter near-miss moments when a bonus is close.',
    ],
    sections: [
      {
        heading: 'What to Expect',
        bullets: [
          'Bomb multipliers appear only in Free Spins, with values up to 100x. They stay on screen during tumbles, then are added together and applied to that tumble’s total win.',
          'Four or more scatters unlock 10 Free Spins. Three or more scatters during the bonus award five more spins, and this can happen multiple times.',
          'Ante Bet raises the stake from 20x to 25x, doubles the Free Spins entry chance in the base game, and disables Buy Free Spins. Buy Free Spins is available at the price set in game config.',
        ],
      },
      {
        heading: 'Free Spins',
        paragraphs: [
          'Landing four or more Scatter symbols in the base game awards 10 Free Spins. Scatters can appear on all reels.',
          'During Free Spins, bomb multiplier symbols can land with a random value from 2x, 3x, 4x, 5x, 6x, 8x, 10x, 12x, 15x, 20x, 25x, 50x, or 100x. They do not appear in the base game. Multipliers remain until the current tumble sequence ends. All multiplier values on screen are then added together, and that combined value multiplies the tumble’s total win.',
          'Three or more Scatters during Free Spins award five additional Free Spins. Retriggers can happen more than once. The Free Spins win is paid after the round completes.',
          'If the win reaches 10,000x the base bet, the round ends immediately, the cap is paid, and any remaining Free Spins are forfeited.',
          ANTE_BET,
          BUY_FREE_SPINS,
        ],
      },
    ],
  },

  'farm-plush': {
    lead: 'Settle in on a cozy 3×3 farm: five paylines, a respin when two reels match, and a Fortune Wheel when the grid fills. Wins up to 5,000x.',
    intro: [
      'Farm Plush is a 3×3 slot with 5 fixed paylines, left to right. Only the highest win per line is paid; multiple line wins are added together. Wild substitutes for any paying symbol. There are no Free Spins, no bonus buy, and no ante. The game is portrait-first.',
    ],
    sections: [
      {
        heading: 'What to Expect',
        bullets: [
          'When two full reels land the same symbol and the third does not, those reels lock and the remaining reel respins until a payline wins.',
          'Fill all nine positions with a single type (Wilds may help) to spin the Fortune Wheel. Lower tier: 2×, 3×, 4×, 5×, or UP. Upper tier: 7×, 8×, 10×, 12×, or 20×.',
          'Maximum win is 5,000x total bet. Low volatility. No Free Spins, buy, or ante.',
        ],
      },
      {
        heading: 'Respin and Fortune Wheel',
        paragraphs: [
          '**Respin.** If exactly two reels are full stacks of the same symbol and the third is not, those two reels lock. The remaining reel respins until at least one payline wins. If the initial grid already has a line win, respin is skipped. Then all five paylines are evaluated on the final grid.',
          '**Fortune Wheel.** If the final grid is all one symbol type (Wilds may complete it, including nine Wilds), the Fortune Wheel spins and multiplies the total line wins. Landing on UP advances to the higher multiplier tier.',
          'If the win reaches 5,000x total bet, the round ends and the cap is paid.',
        ],
      },
    ],
  },

  'fishland-treasure': {
    lead: 'Dive a 6×5, 50-line underwater hunt: tumble line wins, fill the treasure chest, then collect Gold Coins on a dedicated bonus grid. Wins up to 10,000x.',
    intro: [
      'Fishland Treasure is a 6×5 slot with 50 fixed paylines, left to right. Winning symbols tumble away so new symbols can drop in and form more line wins from the same spin. Wilds substitute on reels 2, 3, and 4 in the base game. Scatters unlock Free Spins with Multiplier Wilds. Gold Coins and Collect symbols can pay instantly, and filling the treasure chest to step 5 starts the Gold Coin Collect bonus.',
    ],
    sections: [
      {
        heading: 'What to Expect',
        bullets: [
          'Gold Coins carry prizes from 0.5x to 20x total bet. Collect symbols gather those prizes on the main grid. Filling the treasure chest to step 5 starts a 6-spin Gold Coin Collect bonus.',
          'On the bonus grid, reels 1 and 6 show only Collect or empty (at least one Collect is guaranteed). Reels 2–5 show only Gold Coins or empty. The bonus cannot be extended.',
          'Free Spins use Multiplier Wilds (up to 50x). When more than one helps the same line, their values are added, then that line win is multiplied. Ante Bet doubles the Free Spins entry chance in the base game and disables buy. Buy Free Spins is available at the price set in game config.',
        ],
      },
      {
        heading: 'Gold Coin Collect and Free Spins',
        paragraphs: [
          '**Base game.** Gold Coin symbols land with a prize as a multiple of total bet. If Collect lands with coins on screen, those prizes are gathered immediately. Coins also fill a treasure chest beside the grid (steps 0–5).',
          '**Gold Coin Collect bonus.** After the chest reaches step 5 and explodes during a base-game round, the game switches to the Gold Collect grid for 6 spins. Reels 1 and 6 can show only Collect or empty cells, and together they always guarantee at least one Collect. Reels 2–5 can show only Gold Coin or empty cells, with a higher chance to land coins. Coin values are 0.5×, 1×, 1.5×, 2×, 2.5×, 3×, 3.5×, 4×, 5×, 6×, 7×, 8×, 9×, 10×, 12×, 15×, and 20× total bet. The feature ends after the awarded spins and cannot be extended.',
          '**Free Spins.** Enough Scatters after the reels settle start Free Spins. A natural 4-Scatter trigger (and Buy Free Spins) awards 10 Free Spins. Extra Scatters during the feature can add more spins. During Free Spins, Multiplier Wilds can land with 2x, 3x, 4x, 5x, 6x, 8x, 10x, 12x, 15x, 20x, 25x, or 50x. They help complete paylines and multiply those line wins. Gold Coin and Collect do not appear in Free Spins, so the chest cannot progress there.',
          ANTE_BET,
          '**Buy Free Spins** instantly starts a Free Spins round from the base game at the price set in game config, matching a natural 4-Scatter trigger. Ante Bet must be off.',
          'If the win reaches 10,000x the bet, the round ends and the cap is paid.',
        ],
      },
    ],
  },

  'flaming-phoenix-rebirth': {
    lead: 'Rise on a 6×5, 50-line fire grid: tumble line wins, fill the treasure chest, then collect Gold Coins on a dedicated bonus grid. Wins up to 10,000x.',
    intro: [
      'Flaming Phoenix: Rebirth is a 6×5 slot with 50 fixed paylines, left to right. Winning symbols tumble away so new symbols can drop in and form more line wins from the same spin. Wilds substitute on reels 2, 3, and 4 in the base game. Scatters unlock Free Spins with Multiplier Wilds. Gold Coins and Collect symbols can pay instantly, and filling the treasure chest to step 5 starts the Gold Coin Collect bonus.',
    ],
    sections: [
      {
        heading: 'What to Expect',
        bullets: [
          'Gold Coins carry prizes from 0.5x to 20x total bet. Collect symbols gather those prizes on the main grid. Filling the treasure chest to step 5 starts a 6-spin Gold Coin Collect bonus.',
          'On the bonus grid, reels 1 and 6 show only Collect or empty (at least one Collect is guaranteed). Reels 2–5 show only Gold Coins or empty. The bonus cannot be extended.',
          'Free Spins use Multiplier Wilds (up to 50x). When more than one helps the same line, their values are added, then that line win is multiplied. Ante Bet doubles the Free Spins entry chance in the base game and disables buy. Buy Free Spins is available at the price set in game config.',
        ],
      },
      {
        heading: 'Gold Coin Collect and Free Spins',
        paragraphs: [
          '**Base game.** Gold Coin symbols land with a prize as a multiple of total bet. If Collect lands with coins on screen, those prizes are gathered immediately. Coins also fill a treasure chest beside the grid (steps 0–5).',
          '**Gold Coin Collect bonus.** After the chest reaches step 5 and explodes during a base-game round, the game switches to the Gold Collect grid for 6 spins. Reels 1 and 6 can show only Collect or empty cells, and together they always guarantee at least one Collect. Reels 2–5 can show only Gold Coin or empty cells, with a higher chance to land coins. Coin values are 0.5×, 1×, 1.5×, 2×, 2.5×, 3×, 3.5×, 4×, 5×, 6×, 7×, 8×, 9×, 10×, 12×, 15×, and 20× total bet. The feature ends after the awarded spins and cannot be extended.',
          '**Free Spins.** Enough Scatters after the reels settle start Free Spins. A natural 4-Scatter trigger (and Buy Free Spins) awards 10 Free Spins. Extra Scatters during the feature can add more spins. During Free Spins, only paying symbols and Multiplier Wilds appear — Gold Coin and Collect do not, so the chest cannot progress. Multiplier Wilds can land with 2x, 3x, 4x, 5x, 6x, 8x, 10x, 12x, 15x, 20x, 25x, or 50x. When more than one helps the same win, their values are added together for that winning line.',
          ANTE_BET,
          '**Buy Free Spins** instantly starts a Free Spins round from the base game at the price set in game config, matching a natural 4-Scatter trigger. Ante Bet must be off.',
          'If the win reaches 10,000x the bet, the round ends and the cap is paid.',
        ],
      },
    ],
  },

  'gemstone-alchemist': {
    lead: 'Unlock the top row of a 6×6 pay-anywhere grid, tumble matching gems, and stack multipliers in Free Spins for wins up to 25,000x.',
    intro: [
      'Gemstone Alchemist is a 6×6 pay-anywhere slot. Identical symbols pay by how many land anywhere on the playable screen after a spin or tumble. The top row starts locked. Each winning tumble counts toward unlocking those cells, left to right, expanding the grid from 30 to 36 positions. Four or more scatters unlock 10 Free Spins, where multiplier symbols stay through the tumble, add together, and boost that sequence’s total win. Unlocked top cells stay open for the rest of the Free Spins round.',
    ],
    sections: [
      {
        heading: 'What to Expect',
        bullets: [
          'The locked top row unlocks during tumbles at counts 1, 3, 6, 9, 12, and 15. Unlocks persist through Free Spins and reset on a new base-game spin.',
          'Multiplier symbols appear only in Free Spins (up to 500x). They stay on screen during tumbles, then are added together and applied to that tumble’s total win.',
          'Four or more scatters unlock 10 Free Spins. Three or more scatters during the bonus award five more spins. Ante Bet raises the stake from 20x to 25x and doubles the Free Spins entry chance in the base game. Buy Free Spins is available at the price set in game config.',
        ],
      },
      {
        heading: 'Unlock and Free Spins',
        paragraphs: [
          'The grid is 6 reels × 6 rows. At the start of each base-game spin the top row is locked (30 playable cells). Winning tumbles raise a tumble count. Columns open left to right when that count reaches 1, 3, 6, 9, 12, and 15. Newly unlocked cells can land symbols and pay. A new paid base spin relocks the top row. During Free Spins, unlocked cells stay open for the rest of the round.',
          'Landing four or more Scatter symbols in the base game awards 10 Free Spins. During Free Spins, multiplier symbols can land with a random value from 2x, 3x, 4x, 5x, 6x, 8x, 10x, 12x, 15x, 20x, 25x, 50x, 100x, 200x, or 500x. They do not appear in the base game. Multipliers remain until the current tumble sequence ends. All multiplier values on screen are then added together, and that combined value multiplies the tumble’s total win.',
          'Three or more Scatters during Free Spins award five additional Free Spins. Retriggers can happen more than once.',
          'If the win reaches 25,000x the base bet, the round ends immediately, the cap is paid, and any remaining Free Spins are forfeited.',
          ANTE_BET,
          BUY_FREE_SPINS,
        ],
      },
    ],
  },

  'inannas-quest': {
    lead: "Follow the goddess Inanna across a 6×5 pay-anywhere grid, tumble matching relics, and stack multipliers in Free Spins for wins up to 10,000x.",
    intro: [
      "Inanna's Quest is a 6×5 pay-anywhere slot. Identical symbols pay by how many land anywhere on the screen after a spin or tumble — no paylines required. Winning symbols disappear, the rest fall, and new symbols drop in for as long as wins keep forming. Four or more scatters unlock 10 Free Spins, where multiplier symbols (up to 100x each) stay through the tumble, add together, and boost that sequence’s total win. Inanna hosts the grid, including gold-scatter near-miss moments when a bonus is close.",
    ],
    sections: [
      {
        heading: 'What to Expect',
        bullets: [
          'Multiplier symbols appear only in Free Spins, with values up to 100x. They stay on screen during tumbles, then are added together and applied to that tumble’s total win.',
          'Four or more scatters unlock 10 Free Spins. Three or more scatters during the bonus award five more spins, and this can happen multiple times.',
          'Ante Bet raises the stake from 20x to 25x, doubles the Free Spins entry chance in the base game, and disables Buy Free Spins. Buy Free Spins is available at the price set in game config.',
        ],
      },
      {
        heading: 'Free Spins',
        paragraphs: [
          'Landing four or more Scatter symbols in the base game awards 10 Free Spins. Scatters can appear on all reels.',
          'During Free Spins, multiplier symbols can land with a random value from 2x, 3x, 4x, 5x, 6x, 8x, 10x, 12x, 15x, 20x, 25x, 50x, or 100x. They do not appear in the base game. Multipliers remain until the current tumble sequence ends. All multiplier values on screen are then added together, and that combined value multiplies the tumble’s total win.',
          'Three or more Scatters during Free Spins award five additional Free Spins. Retriggers can happen more than once. The Free Spins win is paid after the round completes.',
          'If the win reaches 10,000x the base bet, the round ends immediately, the cap is paid, and any remaining Free Spins are forfeited.',
          ANTE_BET,
          BUY_FREE_SPINS,
        ],
      },
    ],
  },

  'midnight-cabaret': {
    lead: 'Take the stage on a 5×3 cabaret grid: 25 fixed paylines, a giant 3×3 block in Free Spins, and Money Spins with MINI, MAJOR, and MEGA prizes. Wins up to 5,000x.',
    intro: [
      'Midnight Cabaret is a 5×3 slot with 25 fixed paylines, left to right from the leftmost reel. Money symbols can lock prizes and start Money Spins. Scatters unlock Free Spins, where a giant 3×3 symbol block covers the center of the reels. If both features hit on the same spin, Money Spins play first; if Money Spins land during Free Spins, Free Spins continue afterward.',
    ],
    sections: [
      {
        heading: 'What to Expect',
        bullets: [
          'Six or more Money symbols trigger Money Spins. Prizes stay locked while the remaining positions respin. Each new Money symbol refreshes 3 chances. Filling the grid awards MEGA (1,000x).',
          'Jackpot prizes: MINI 50x, MAJOR 100x, MEGA 1,000x (fill the feature grid).',
          'Free Spins add a giant 3×3 symbol block in the center. Ante Bet doubles the Free Spins entry chance in the base game. Buy Free Spins starts the bonus at the price set in game config.',
        ],
      },
      {
        heading: 'Money Spins and Free Spins',
        paragraphs: [
          '**Money Spins.** Landing 6 or more Money symbols starts the feature with 3 chances. Money prizes stay in place. Empty positions respin. A new Money symbol locks in and refreshes chances to 3. The round ends when chances run out or the grid is full. Jackpots: MINI 50x, MAJOR 100x, MEGA 1,000x for filling the feature grid.',
          '**Free Spins.** Scatter symbols unlock Free Spins. The round awards 10 Free Spins, with extra spins available on retrigger. A giant 3×3 block covers the center reels (2–4, all rows); the side cells keep spinning.',
          FEATURE_ORDER,
          BUY_FREE_SPINS,
          ANTE_BET,
          'If the win reaches 5,000x the bet, the round ends and the cap is paid.',
        ],
      },
    ],
  },

  'mytic-forge': {
    lead: 'Unlock the top row of a 6×6 pay-anywhere grid, tumble matching relics, and stack multipliers in Free Spins for wins up to 25,000x.',
    intro: [
      'Mytic Forge is a 6×6 pay-anywhere slot. Identical symbols pay by how many land anywhere on the playable screen after a spin or tumble. The top row starts locked. Each winning tumble counts toward unlocking those cells, left to right, expanding the grid from 30 to 36 positions. Four or more scatters unlock 10 Free Spins, where multiplier symbols stay through the tumble, add together, and boost that sequence’s total win. Unlocked top cells stay open for the rest of the Free Spins round.',
    ],
    sections: [
      {
        heading: 'What to Expect',
        bullets: [
          'The locked top row unlocks during tumbles at counts 1, 3, 6, 9, 12, and 15. Unlocks persist through Free Spins and reset on a new base-game spin.',
          'Multiplier symbols appear only in Free Spins (up to 500x). They stay on screen during tumbles, then are added together and applied to that tumble’s total win.',
          'Four or more scatters unlock 10 Free Spins. Three or more scatters during the bonus award five more spins. Ante Bet raises the stake from 20x to 25x and doubles the Free Spins entry chance in the base game. Buy Free Spins is available at the price set in game config.',
        ],
      },
      {
        heading: 'Unlock and Free Spins',
        paragraphs: [
          'The grid is 6 reels × 6 rows. At the start of each base-game spin the top row is locked (30 playable cells). Winning tumbles raise a tumble count. Columns open left to right when that count reaches 1, 3, 6, 9, 12, and 15. Newly unlocked cells can land symbols and pay. A new paid base spin relocks the top row. During Free Spins, unlocked cells stay open for the rest of the round.',
          'Landing four or more Scatter symbols in the base game awards 10 Free Spins. During Free Spins, multiplier symbols can land with a random value from 2x, 3x, 4x, 5x, 6x, 8x, 10x, 12x, 15x, 20x, 25x, 50x, 100x, 200x, or 500x. They do not appear in the base game. Multipliers remain until the current tumble sequence ends. All multiplier values on screen are then added together, and that combined value multiplies the tumble’s total win.',
          'Three or more Scatters during Free Spins award five additional Free Spins. Retriggers can happen more than once.',
          'If the win reaches 25,000x the base bet, the round ends immediately, the cap is paid, and any remaining Free Spins are forfeited.',
          ANTE_BET,
          BUY_FREE_SPINS,
        ],
      },
    ],
  },

  'panthers-reign-jungle-king': {
    lead: 'Hunt with the jungle king on a 6×5 pay-anywhere grid, tumble matching animals, and stack multipliers in Free Spins for wins up to 10,000x.',
    intro: [
      "Panther's Reign: Jungle King is a 6×5 pay-anywhere slot. Identical symbols pay by how many land anywhere on the screen after a spin or tumble — no paylines required. Winning symbols disappear, the rest fall, and new symbols drop in for as long as wins keep forming. Four or more scatters unlock 10 Free Spins, where multiplier symbols (up to 100x each) stay through the tumble, add together, and boost that sequence’s total win. The panther hosts the grid, including gold-scatter near-miss moments when a bonus is close.",
    ],
    sections: [
      {
        heading: 'What to Expect',
        bullets: [
          'Multiplier symbols appear only in Free Spins, with values up to 100x. They stay on screen during tumbles, then are added together and applied to that tumble’s total win.',
          'Four or more scatters unlock 10 Free Spins. Three or more scatters during the bonus award five more spins, and this can happen multiple times.',
          'Ante Bet raises the stake from 20x to 25x, doubles the Free Spins entry chance in the base game, and disables Buy Free Spins. Buy Free Spins is available at the price set in game config.',
        ],
      },
      {
        heading: 'Free Spins',
        paragraphs: [
          'Landing four or more Scatter symbols in the base game awards 10 Free Spins. Scatters can appear on all reels.',
          'During Free Spins, multiplier symbols can land with a random value from 2x, 3x, 4x, 5x, 6x, 8x, 10x, 12x, 15x, 20x, 25x, 50x, or 100x. They do not appear in the base game. Multipliers remain until the current tumble sequence ends. All multiplier values on screen are then added together, and that combined value multiplies the tumble’s total win.',
          'Three or more Scatters during Free Spins award five additional Free Spins. Retriggers can happen more than once. The Free Spins win is paid after the round completes.',
          'If the win reaches 10,000x the base bet, the round ends immediately, the cap is paid, and any remaining Free Spins are forfeited.',
          ANTE_BET,
          BUY_FREE_SPINS,
        ],
      },
    ],
  },

  'pharaohs-codex': {
    lead: "Read the codex on a 6×5, 50-line Egyptian grid: tumble line wins, fill the treasure chest, then collect Gold Coins on a dedicated bonus grid. Wins up to 10,000x.",
    intro: [
      "Pharaoh's Codex is a 6×5 slot with 50 fixed paylines, left to right. Winning symbols tumble away so new symbols can drop in and form more line wins from the same spin. Wilds substitute on reels 2, 3, and 4 in the base game. Scatters unlock Free Spins with Multiplier Wilds. Gold Coins and Collect symbols can pay instantly, and filling the treasure chest to step 5 starts the Gold Coin Collect bonus.",
    ],
    sections: [
      {
        heading: 'What to Expect',
        bullets: [
          'Gold Coins carry prizes from 0.5x to 20x total bet. Collect symbols gather those prizes on the main grid. Filling the treasure chest to step 5 starts a 6-spin Gold Coin Collect bonus.',
          'On the bonus grid, reels 1 and 6 show only Collect or empty (at least one Collect is guaranteed). Reels 2–5 show only Gold Coins or empty. The bonus cannot be extended.',
          'Free Spins use Multiplier Wilds (up to 50x). When more than one helps the same line, their values are added, then that line win is multiplied. Ante Bet doubles the Free Spins entry chance in the base game and disables buy. Buy Free Spins is available at the price set in game config.',
        ],
      },
      {
        heading: 'Gold Coin Collect and Free Spins',
        paragraphs: [
          '**Base game.** Gold Coin symbols land with a prize as a multiple of total bet. If Collect lands with coins on screen, those prizes are gathered immediately. Coins also fill a treasure chest beside the grid (steps 0–5).',
          '**Gold Coin Collect bonus.** After the chest reaches step 5 and explodes during a base-game round, the game switches to the Gold Collect grid for 6 spins. Reels 1 and 6 can show only Collect or empty cells, and together they always guarantee at least one Collect. Reels 2–5 can show only Gold Coin or empty cells, with a higher chance to land coins. Coin values are 0.5×, 1×, 1.5×, 2×, 2.5×, 3×, 3.5×, 4×, 5×, 6×, 7×, 8×, 9×, 10×, 12×, 15×, and 20× total bet. The feature ends after the awarded spins and cannot be extended.',
          '**Free Spins.** Enough Scatters after the reels settle start Free Spins. A natural 4-Scatter trigger (and Buy Free Spins) awards 10 Free Spins. Extra Scatters during the feature can add more spins. During Free Spins, only paying symbols and Multiplier Wilds appear — Gold Coin and Collect do not, so the chest cannot progress. Multiplier Wilds can land with 2x, 3x, 4x, 5x, 6x, 8x, 10x, 12x, 15x, 20x, 25x, or 50x. When more than one helps the same win, their values are added together for that winning line.',
          ANTE_BET,
          '**Buy Free Spins** instantly starts a Free Spins round from the base game at the price set in game config, matching a natural 4-Scatter trigger. Ante Bet must be off.',
          'If the win reaches 10,000x the bet, the round ends and the cap is paid.',
        ],
      },
    ],
  },

  'private-room': {
    lead: 'Step into a private vault on a 5×3 grid: 25 fixed paylines, a giant 3×3 block in Free Spins, and Money Spins with MINI, MAJOR, and MEGA prizes. Wins up to 5,000x.',
    intro: [
      'Private Room is a 5×3 slot with 25 fixed paylines, left to right from the leftmost reel. Money symbols can lock prizes and start Money Spins. Scatters unlock Free Spins, where a giant 3×3 symbol block covers the center of the reels. If both features hit on the same spin, Money Spins play first; if Money Spins land during Free Spins, Free Spins continue afterward.',
    ],
    sections: [
      {
        heading: 'What to Expect',
        bullets: [
          'Six or more Money symbols trigger Money Spins. Prizes stay locked while the remaining positions respin. Each new Money symbol refreshes 3 chances. Filling the grid awards MEGA (1,000x).',
          'Jackpot prizes: MINI 50x, MAJOR 100x, MEGA 1,000x (fill the feature grid).',
          'Free Spins add a giant 3×3 symbol block in the center. During Free Spins, Money on the giant stays mirrored on that block. Ante Bet doubles the Free Spins entry chance in the base game. Buy Free Spins starts the bonus at the price set in game config.',
        ],
      },
      {
        heading: 'Money Spins and Free Spins',
        paragraphs: [
          '**Money Spins.** Landing 6 or more Money symbols starts the feature with 3 chances. Money prizes stay in place. Empty positions respin. A new Money symbol locks in and refreshes chances to 3. The round ends when chances run out or the grid is full. Jackpots: MINI 50x, MAJOR 100x, MEGA 1,000x for filling the feature grid.',
          "**Free Spins.** Scatter symbols unlock Free Spins. The round awards 10 Free Spins, with extra spins available on retrigger. A giant 3×3 block covers the center reels (2–4, all rows); the side cells keep spinning. If Money Spins start during Free Spins, money on the giant’s center cell is mirrored onto the giant so the block stays in front.",
          FEATURE_ORDER,
          BUY_FREE_SPINS,
          ANTE_BET,
          'If the win reaches 5,000x the bet, the round ends and the cap is paid.',
        ],
      },
    ],
  },

  'spin-plush': {
    lead: 'Haunt a 3×3 plush grid: five paylines, a respin when two reels match, and a Fortune Wheel when the screen fills. Wins up to 5,000x.',
    intro: [
      'Spin Plush is a spooky 3×3 slot with 5 fixed paylines, left to right. Only the highest win per line is paid; multiple line wins are added together. Wild substitutes for any paying symbol. There are no Free Spins, no bonus buy, and no ante. The game is portrait-first.',
    ],
    sections: [
      {
        heading: 'What to Expect',
        bullets: [
          'When two full reels land the same symbol and the third does not, those reels lock and the remaining reel respins until a payline wins.',
          'Fill all nine positions with a single type (Wilds may help) to spin the Fortune Wheel. Lower tier: 2×, 3×, 4×, 5×, or UP. Upper tier: 7×, 8×, 10×, 12×, or 20×.',
          'Maximum win is 5,000x total bet. Low volatility. No Free Spins, buy, or ante.',
        ],
      },
      {
        heading: 'Respin and Fortune Wheel',
        paragraphs: [
          '**Respin.** If exactly two reels are full stacks of the same symbol and the third is not, those two reels lock. The remaining reel respins until at least one payline wins. If the initial grid already has a line win, respin is skipped. Then all five paylines are evaluated on the final grid.',
          '**Fortune Wheel.** If the final grid is all one symbol type (Wilds may complete it, including nine Wilds), the Fortune Wheel spins and multiplies the total line wins. Landing on UP advances to the higher multiplier tier.',
          'If the win reaches 5,000x total bet, the round ends and the cap is paid.',
        ],
      },
    ],
  },

  'temptation-garden': {
    lead: 'Wander a romantic 3×3 garden: five paylines, a respin when two reels match, and a Fortune Wheel when the screen fills. Wins up to 5,000x.',
    intro: [
      'Temptation Garden is a 3×3 slot with 5 fixed paylines, left to right. Only the highest win per line is paid; multiple line wins are added together. Wild substitutes for any paying symbol. There are no Free Spins, no bonus buy, and no ante. The game is portrait-first.',
    ],
    sections: [
      {
        heading: 'What to Expect',
        bullets: [
          'When two full reels land the same symbol and the third does not, those reels lock and the remaining reel respins until a payline wins.',
          'Fill all nine positions with a single type (Wilds may help) to spin the Fortune Wheel. Lower tier: 2×, 3×, 4×, 5×, or UP. Upper tier: 7×, 8×, 10×, 12×, or 20×.',
          'Maximum win is 5,000x total bet. Low volatility. No Free Spins, buy, or ante.',
        ],
      },
      {
        heading: 'Respin and Fortune Wheel',
        paragraphs: [
          '**Respin.** If exactly two reels are full stacks of the same symbol and the third is not, those two reels lock. The remaining reel respins until at least one payline wins. If the initial grid already has a line win, respin is skipped. Then all five paylines are evaluated on the final grid.',
          '**Fortune Wheel.** If the final grid is all one symbol type (Wilds may complete it, including nine Wilds), the Fortune Wheel spins and multiplies the total line wins. Landing on UP advances to the higher multiplier tier.',
          'If the win reaches 5,000x total bet, the round ends and the cap is paid.',
        ],
      },
    ],
  },

  'treasure-of-the-lost-temple': {
    lead: 'Explore a lost temple on a 6×5 pay-anywhere grid, tumble matching relics, and stack multipliers in Free Spins for wins up to 10,000x.',
    intro: [
      'Treasure of the Lost Temple is a 6×5 pay-anywhere slot. Identical symbols pay by how many land anywhere on the screen after a spin or tumble — no paylines required. Winning symbols disappear, the rest fall, and new symbols drop in for as long as wins keep forming. Four or more scatters unlock 10 Free Spins, where multiplier symbols (up to 100x each) stay through the tumble, add together, and boost that sequence’s total win. An explorer hosts the grid, including scatter near-miss moments when a bonus is close.',
    ],
    sections: [
      {
        heading: 'What to Expect',
        bullets: [
          'Multiplier symbols appear only in Free Spins, with values up to 100x. They stay on screen during tumbles, then are added together and applied to that tumble’s total win.',
          'Four or more scatters unlock 10 Free Spins. Four or more scatters during the bonus award five more spins, and this can happen multiple times.',
          'Ante Bet raises the stake from 20x to 25x, doubles the Free Spins entry chance in the base game, and disables Buy Free Spins. Buy Free Spins is available at the price set in game config.',
        ],
      },
      {
        heading: 'Free Spins',
        paragraphs: [
          'Landing four or more Scatter symbols in the base game awards 10 Free Spins. Scatters can appear on all reels.',
          'During Free Spins, multiplier symbols can land with a random value from 2x, 3x, 4x, 5x, 6x, 8x, 10x, 12x, 15x, 20x, 25x, 50x, or 100x. They do not appear in the base game. Multipliers remain until the current tumble sequence ends. All multiplier values on screen are then added together, and that combined value multiplies the tumble’s total win.',
          'Four or more Scatters during Free Spins award five additional Free Spins. Retriggers can happen more than once. The Free Spins win is paid after the round completes.',
          'If the win reaches 10,000x the base bet, the round ends immediately, the cap is paid, and any remaining Free Spins are forfeited.',
          ANTE_BET,
          BUY_FREE_SPINS,
        ],
      },
    ],
  },

  'wukongs-treasure': {
    lead: "Unlock the top row of a 6×6 pay-anywhere grid, tumble matching relics, and stack multipliers in Free Spins for wins up to 25,000x.",
    intro: [
      "Wukong's Treasure is a 6×6 pay-anywhere slot. Identical symbols pay by how many land anywhere on the playable screen after a spin or tumble. The top row starts locked. Each winning tumble counts toward unlocking those cells, left to right, expanding the grid from 30 to 36 positions. Four or more scatters unlock 10 Free Spins, where multiplier symbols stay through the tumble, add together, and boost that sequence’s total win. Unlocked top cells stay open for the rest of the Free Spins round.",
    ],
    sections: [
      {
        heading: 'What to Expect',
        bullets: [
          'The locked top row unlocks during tumbles at counts 1, 3, 6, 9, 12, and 15. Unlocks persist through Free Spins and reset on a new base-game spin.',
          'Multiplier symbols appear only in Free Spins (up to 500x). They stay on screen during tumbles, then are added together and applied to that tumble’s total win.',
          'Four or more scatters unlock 10 Free Spins. Three or more scatters during the bonus award five more spins. Ante Bet raises the stake from 20x to 25x and doubles the Free Spins entry chance in the base game. Buy Free Spins is available at the price set in game config.',
        ],
      },
      {
        heading: 'Unlock and Free Spins',
        paragraphs: [
          'The grid is 6 reels × 6 rows. At the start of each base-game spin the top row is locked (30 playable cells). Winning tumbles raise a tumble count. Columns open left to right when that count reaches 1, 3, 6, 9, 12, and 15. Newly unlocked cells can land symbols and pay. A new paid base spin relocks the top row. During Free Spins, unlocked cells stay open for the rest of the round.',
          'Landing four or more Scatter symbols in the base game awards 10 Free Spins. During Free Spins, multiplier symbols can land with a random value from 2x, 3x, 4x, 5x, 6x, 8x, 10x, 12x, 15x, 20x, 25x, 50x, 100x, 200x, or 500x. They do not appear in the base game. Multipliers remain until the current tumble sequence ends. All multiplier values on screen are then added together, and that combined value multiplies the tumble’s total win.',
          'Three or more Scatters during Free Spins award five additional Free Spins. Retriggers can happen more than once.',
          'If the win reaches 25,000x the base bet, the round ends immediately, the cap is paid, and any remaining Free Spins are forfeited.',
          ANTE_BET,
          BUY_FREE_SPINS,
        ],
      },
    ],
  },
};

/** Catalog ids that do not slugify onto the editorial key. */
const COPY_ALIASES: Record<string, string> = {
  'mythic-forge': 'mytic-forge',
  'panthers-reign': 'panthers-reign-jungle-king',
  'casineon-royal': 'casino-neon',
  casineon: 'casino-neon',
};

/**
 * camelCase symbols and spaced titles reduce to one key. Only a namespace
 * prefix is dropped ("richyplay:catPlush"), never a subtitle ("Phoenix: Rebirth").
 */
function slugify(value: string): string {
  return value
    .replace(/^[a-z0-9_-]+:(?=\S)/i, '')
    .replace(/['’]/g, '')
    .replace(/([a-z0-9])([A-Z])/g, '$1-$2')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export function slotCopy(game: HubGame): SlotCopy | null {
  if (game.category === 'live') return null;

  for (const candidate of [game.title, game.id, game.details?.symbol, game.sourceGameId]) {
    if (!candidate) continue;
    const key = slugify(String(candidate));
    const copy = SLOT_COPY[key] ?? SLOT_COPY[COPY_ALIASES[key]];
    if (copy) return copy;
  }

  return null;
}
